import { useCallback, useState } from 'react';

/**
 * Hook that manages server-side pagination and sorting state for Ant Design Tables.
 *
 * Usage:
 *   const { queryParams, paginationConfig, handleTableChange } = useServerPagination({
 *     defaultSortBy: 'facilityName',
 *   });
 *
 *   useGetFacilitiesListQuery({ portfolioId, ...queryParams });
 *
 *   <Table onChange={handleTableChange} pagination={paginationConfig(totalElements)} />
 *
 * @param {Object} options
 * @param {string} [options.defaultSortBy='id'] - default sort field
 * @param {string} [options.defaultSortDir='asc'] - default sort direction
 * @param {number} [options.defaultPageSize=10] - default page size
 */
const useServerPagination = ({
  defaultSortBy = 'id',
  defaultSortDir = 'asc',
  defaultPageSize = 10,
} = {}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [sortBy, setSortBy] = useState(defaultSortBy);
  const [sortDir, setSortDir] = useState(defaultSortDir);

  const handleTableChange = useCallback(
    (pagination, _filters, sorter) => {
      setCurrentPage(pagination.current);
      setPageSize(pagination.pageSize);

      if (sorter.field && sorter.order) {
        setSortBy(sorter.field);
        setSortDir(sorter.order === 'descend' ? 'desc' : 'asc');
        setCurrentPage(1);
      } else if (sorter.field && !sorter.order) {
        setSortBy(defaultSortBy);
        setSortDir(defaultSortDir);
        setCurrentPage(1);
      }
    },
    [defaultSortBy, defaultSortDir]
  );

  const resetPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  const queryParams = {
    page: currentPage,
    size: pageSize,
    sortBy,
    sortDir,
  };

  const paginationConfig = useCallback(
    (totalElements) => ({
      current: currentPage,
      pageSize,
      total: totalElements || 0,
      showSizeChanger: true,
      showTotal: (total, range) => `Showing ${range[0]} to ${range[1]} of ${total} entries`,
    }),
    [currentPage, pageSize]
  );

  return {
    currentPage,
    pageSize,
    sortBy,
    sortDir,
    queryParams,
    paginationConfig,
    handleTableChange,
    resetPage,
  };
};

export default useServerPagination;
